<template>
  <div class="em-page">
    <div v-if="loading" class="em-state">Loading…</div>
    <div v-if="error" class="em-state em-error">
      <i class="mdi mdi-alert-circle"></i>
      <h2>{{ error }}</h2>
    </div>

    <div v-if="!loading && data" class="em-card">
      <header class="em-header">
        <div class="em-emergency-badge"><i class="mdi mdi-medical-bag"></i> EMERGENCY ID</div>
        <div class="em-fac-name">{{ data.Facility.hospital_name }}</div>
      </header>

      <!-- Patient identity (LARGE, top) -->
      <section class="em-patient">
        <h1 class="em-name">{{ data.Patient.name }}</h1>
        <div class="em-id-row">
          <div class="em-id-block">
            <div class="em-id-label">Age</div>
            <div class="em-id-value">{{ data.Patient.age }} yrs</div>
          </div>
          <div class="em-id-block">
            <div class="em-id-label">Sex</div>
            <div class="em-id-value">{{ data.Patient.sex }}</div>
          </div>
          <div class="em-id-block em-blood">
            <div class="em-id-label">Blood</div>
            <div class="em-id-value">{{ data.Patient.blood_group }}</div>
          </div>
          <div class="em-id-block">
            <div class="em-id-label">Patient #</div>
            <div class="em-id-value">{{ data.Patient.patient_no }}</div>
          </div>
        </div>
      </section>

      <!-- Allergies — RED, biggest -->
      <section class="em-section em-allergies">
        <h2><i class="mdi mdi-alert-octagon"></i> ALLERGIES</h2>
        <div class="em-big-text">{{ data.Critical.allergies }}</div>
      </section>

      <!-- Active programmes / alerts -->
      <section v-if="data.Critical.programmes.length" class="em-section em-alert">
        <h2><i class="mdi mdi-flag"></i> ACTIVE ALERTS</h2>
        <ul>
          <li v-for="(p, i) in data.Critical.programmes" :key="i">{{ p }}</li>
        </ul>
      </section>

      <!-- Chronic conditions -->
      <section class="em-section">
        <h2><i class="mdi mdi-heart-pulse"></i> CHRONIC CONDITIONS</h2>
        <div>{{ data.Critical.chronic_conditions }}</div>
      </section>

      <!-- Current medications -->
      <section v-if="data.Medications.length" class="em-section">
        <h2><i class="mdi mdi-pill"></i> CURRENT MEDICATIONS</h2>
        <ul class="em-rx-list">
          <li v-for="(m, i) in data.Medications" :key="i">
            <strong>{{ m.drug_name }}</strong> {{ m.dosage }} {{ m.frequency }}
          </li>
        </ul>
      </section>

      <!-- Recent diagnoses -->
      <section v-if="data.Diagnoses.length" class="em-section">
        <h2><i class="mdi mdi-clipboard-text"></i> RECENT DIAGNOSES</h2>
        <ul>
          <li v-for="(d, i) in data.Diagnoses" :key="i">
            {{ d.diagnosis_name }}<span v-if="d.icd_code"> ({{ d.icd_code }})</span>
          </li>
        </ul>
      </section>

      <!-- Latest vitals -->
      <section v-if="data.Vitals" class="em-section">
        <h2><i class="mdi mdi-pulse"></i> LATEST VITALS</h2>
        <div class="em-vitals">
          <span v-if="data.Vitals.bp_systolic">BP {{ data.Vitals.bp_systolic }}/{{ data.Vitals.bp_diastolic }}</span>
          <span v-if="data.Vitals.pulse">HR {{ data.Vitals.pulse }}</span>
          <span v-if="data.Vitals.temperature">T {{ data.Vitals.temperature }}°</span>
          <span v-if="data.Vitals.oxygen_saturation">SpO2 {{ data.Vitals.oxygen_saturation }}%</span>
          <span v-if="data.Vitals.weight">{{ data.Vitals.weight }} kg</span>
        </div>
        <div class="em-muted-sm">{{ formatDt(data.Vitals.created_at) }}</div>
      </section>

      <!-- Next of kin -->
      <section class="em-section em-nok">
        <h2><i class="mdi mdi-account-heart"></i> NEXT OF KIN</h2>
        <div class="em-big-text">{{ data.NextOfKin.name || '—' }}</div>
        <div v-if="data.NextOfKin.relationship" class="em-muted-sm">{{ data.NextOfKin.relationship }}</div>
        <a v-if="data.NextOfKin.phone" :href="'tel:' + data.NextOfKin.phone" class="em-call-btn">
          <i class="mdi mdi-phone"></i> Call {{ data.NextOfKin.phone }}
        </a>
      </section>

      <!-- Insurance -->
      <section v-if="data.Insurance.provider" class="em-section">
        <h2><i class="mdi mdi-shield-account"></i> INSURANCE</h2>
        <div>{{ data.Insurance.provider }} <span class="em-muted-sm">{{ data.Insurance.number }}</span></div>
      </section>

      <!-- Hospital + footer -->
      <footer class="em-footer">
        <div class="em-hospital">
          <strong>{{ data.Facility.hospital_name }}</strong>
          <a v-if="data.Facility.hospital_phone" :href="'tel:' + data.Facility.hospital_phone" class="em-call-btn em-call-hospital">
            <i class="mdi mdi-phone"></i> Call hospital
          </a>
        </div>
        <div class="em-disclaimer">
          QR ID: <code>{{ data.QR.short_code }}</code> · Generated {{ formatDate(data.QR.created_at) }} · Scan #{{ data.QR.scan_count }} ·
          For verification call the hospital directly.
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";

export default {
  name: "EmergencyView",
  data() { return { loading: true, error: '', data: null }; },
  mounted() { this.load(); },
  methods: {
    async load() {
      try {
        const r = await axios.post(hms, { function: 'emergencyView', token: this.$route.params.token });
        if (r.data && r.data.success) { this.data = r.data.data; this.loading = false; }
        else { this.error = r.data?.message || 'QR code not found.'; this.loading = false; }
      } catch (e) { this.error = 'Could not load — check your connection.'; this.loading = false; }
    },
    formatDt(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleString(); },
    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleDateString(); }
  }
};
</script>

<style scoped>
.em-page { min-height: 100vh; background: #fee2e2; padding: 16px; font-family: 'Helvetica Neue', sans-serif; }
.em-state { max-width: 500px; margin: 80px auto; text-align: center; color: #475569; font-size: 18px; }
.em-error i { font-size: 60px; color: #dc2626; }
.em-error h2 { color: #991b1b; margin-top: 12px; }

.em-card { max-width: 720px; margin: 0 auto; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,.2); }

.em-header { background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); color: #fff; padding: 14px 22px; display: flex; justify-content: space-between; align-items: center; }
.em-emergency-badge { font-size: 13px; font-weight: 800; letter-spacing: 1.5px; padding: 4px 12px; background: #fff; color: #dc2626; border-radius: 6px; display: inline-flex; gap: 6px; align-items: center; }
.em-fac-name { font-size: 14px; font-weight: 600; }

.em-patient { padding: 24px 22px; background: #fef2f2; border-bottom: 4px solid #dc2626; }
.em-name { font-size: 28px; font-weight: 800; margin: 0; color: #1a202c; line-height: 1.1; }
.em-id-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 14px; }
.em-id-block { background: #fff; padding: 10px 12px; border-radius: 8px; border-left: 4px solid #94a3b8; }
.em-id-block.em-blood { border-left-color: #dc2626; background: linear-gradient(135deg, #fff 0%, #fee2e2 100%); }
.em-id-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: .5px; font-weight: 700; }
.em-id-value { font-size: 18px; font-weight: 700; color: #1a202c; }
.em-blood .em-id-value { color: #dc2626; font-size: 22px; }

.em-section { padding: 16px 22px; border-bottom: 1px solid #e2e8f0; }
.em-section:last-of-type { border-bottom: 0; }
.em-section h2 { font-size: 12px; font-weight: 800; letter-spacing: 1px; color: #475569; text-transform: uppercase; margin: 0 0 10px; display: flex; align-items: center; gap: 6px; }
.em-section h2 i { color: #475569; font-size: 18px; }
.em-section ul { margin: 0; padding-left: 20px; }
.em-section li { padding: 3px 0; font-size: 15px; }

.em-big-text { font-size: 20px; font-weight: 600; color: #1a202c; }

.em-allergies { background: linear-gradient(135deg, #fff 0%, #fef2f2 100%); border-left: 6px solid #dc2626; }
.em-allergies h2 { color: #991b1b; }
.em-allergies h2 i { color: #dc2626; font-size: 22px; }
.em-allergies .em-big-text { color: #991b1b; font-size: 22px; font-weight: 800; }

.em-alert { background: #fffbeb; border-left: 6px solid #d97706; }
.em-alert h2 { color: #92400e; }
.em-alert h2 i { color: #d97706; }
.em-alert li { color: #78350f; font-weight: 600; }

.em-nok { background: #ecfdf5; border-left: 6px solid #059669; }
.em-nok h2 { color: #065f46; }
.em-nok h2 i { color: #059669; }
.em-nok .em-big-text { color: #065f46; }

.em-rx-list li { font-size: 14px; }

.em-vitals { display: flex; gap: 14px; flex-wrap: wrap; font-size: 16px; font-weight: 600; color: #1a202c; }
.em-vitals span { background: #f1f5f9; padding: 4px 10px; border-radius: 6px; }

.em-call-btn { display: inline-flex; align-items: center; gap: 6px; margin-top: 8px; padding: 10px 18px; background: #059669; color: #fff; border-radius: 8px; text-decoration: none; font-weight: 700; font-size: 15px; }
.em-call-btn:hover { background: #047857; }
.em-call-hospital { background: #2c5282; }
.em-call-hospital:hover { background: #1e3a8a; }

.em-footer { padding: 16px 22px; background: #f1f5f9; }
.em-hospital { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.em-disclaimer { font-size: 11px; color: #64748b; text-align: center; }
.em-disclaimer code { background: #fff; padding: 1px 6px; border-radius: 3px; font-size: 11px; }
.em-muted-sm { font-size: 11px; color: #94a3b8; }

@media print {
  .em-page { background: #fff; padding: 0; }
  .em-card { box-shadow: none; max-width: none; }
  .em-call-btn { background: #fff !important; color: #059669 !important; border: 2px solid #059669; }
}
</style>
